import { NoSsr } from "@mui/base";
import { PageType, theme, useResponsive } from "@product/scmp-sdk";
import { useSetAtom } from "jotai";
import { useRouter } from "next/router";
import type { FunctionComponent } from "react";

import { useAppBarSetStickyScrollHeight } from "scmp-app/components/app-bar/hooks/sticky/app-bar-set-sticky-scroll-height";
import { type Props as BaseLinkProps } from "scmp-app/components/common/base-link";
import { IdleQueue } from "scmp-app/components/common/idle-queue";
import { hamburgerMenuAtom } from "scmp-app/components/hamburger-menu/atoms";
import { DefaultHeaderSwapThreshold } from "scmp-app/components/header/consts";
import { HeaderLogo } from "scmp-app/components/section/section-style/styles";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import { section as sectionData } from "scmp-app/data";
import { useEditionValue } from "scmp-app/lib/edition";
import { useCurrentPageType, useGetHomeAsPath } from "scmp-app/lib/router/hooks";
import { page as HomePage } from "scmp-app/pages/home";
import { page as SectionPage } from "scmp-app/pages/section";
import { Variant } from "scmp-app/pages/section/enums";

import IconMenu from "./header-icon/menu.svg";
import { useHeader, useSwapOnScrollEffect } from "./hooks";
import type { StyledLogoLinkProps } from "./styles";
import {
  ActionButton,
  Container,
  HeaderContainer,
  IconMenuContainer,
  IconMenuPlaceholderContainer,
  LeftFeatureContainer,
  MobileStyledLogoLink,
  RightFeatureContainer,
  StyledDynamicHeaderOnelineMenuContainer,
  StyledLogoLink,
  StyledLogoWithText,
  StyledScmpLogoFlag,
  StyledScmpStyleLogoFlag,
  StyledStyleLogoContainer,
  StyledStyleLogoText,
  StyleHeaderLogoContainer,
} from "./styles";
import type { HeaderConfiguration } from "./types";

export type Props = {
  className?: string;
  headerConfiguration?: HeaderConfiguration;
};

export const Header: FunctionComponent<Props> = ({ className, headerConfiguration }) => {
  const { query } = useRouter();
  const edition = useEditionValue();
  const homeAsPath = useGetHomeAsPath();
  const currentPageType = useCurrentPageType();
  const { reference } = useAppBarSetStickyScrollHeight();

  const setHamburgerMenuState = useSetAtom(hamburgerMenuAtom);
  const appBarVariant = headerConfiguration?.appBarVariant;
  const isTabletUp = useResponsive(theme.breakpoints.up("tablet"), false);

  const isMagazineStyle = appBarVariant === "scmp/magazines-style";
  const isSectionStyleMainPage =
    currentPageType === PageType.Section && query.entityId === sectionData.style.entityId;

  const { shouldSwap } = useSwapOnScrollEffect(
    (headerConfiguration?.appBarStickyScrollHeight ?? 0) || DefaultHeaderSwapThreshold,
  );

  const { mastheadQueryParameter, renderFeatures, responsiveLogoVariant } = useHeader(
    shouldSwap,
    headerConfiguration,
  );

  const { pathname: homePagePathname, query: homePageQuery } = HomePage.route({
    edition,
    slide: "main",
  });

  const homepageLogoLinkProps: BaseLinkProps & StyledLogoLinkProps = {
    $responsiveVariants: responsiveLogoVariant,
    $shouldSwap: shouldSwap,
    anchorProps: { "aria-label": "SCMP homepage" },
    as: homeAsPath,
    pathname: homePagePathname,
    query: { ...homePageQuery, ...mastheadQueryParameter },
  };

  const { pathname: styleSectionPagePathname, query: styleSectionPageQuery } = SectionPage.route({
    appBarVariant,
    entityId: sectionData.style.entityId,
    sectionVariant: Variant.Style,
    slide: "main",
  });

  const styleLogoLinkProps: BaseLinkProps & StyledLogoLinkProps = {
    $responsiveVariants: responsiveLogoVariant,
    $shouldSwap: shouldSwap,
    anchorProps: { "aria-label": "SCMP style section" },
    as: sectionData.style.urlAlias,
    pathname: styleSectionPagePathname,
    query: { ...styleSectionPageQuery, ...mastheadQueryParameter },
  };

  const iconMenuResponsiveVariants = {
    desktopUp: "show",
    homeDesktopUp: "hidden",
    largeDesktopUp: "hidden",
    mobileUp: "show",
    tabletUp: "show",
  } as const;

  return (
    <Container $isMagazineStyle={isMagazineStyle} className={className}>
      {isSectionStyleMainPage && (
        <StyleHeaderLogoContainer ref={reference}>
          <HeaderLogo />
        </StyleHeaderLogoContainer>
      )}
      <HeaderContainer>
        <LeftFeatureContainer>
          <IconMenuPlaceholderContainer $responsiveVariants={iconMenuResponsiveVariants}>
            <IdleQueue>
              <ActionButton
                $responsiveVariants={iconMenuResponsiveVariants}
                onClick={() => {
                  setHamburgerMenuState(current => ({
                    ...current,
                    isOpen: !current.isOpen,
                  }));
                  sendGA4Tracking({
                    action: "click",
                    category: "menu",
                    customized_parameters: {
                      action_type: "open",
                      edition,
                      menu_type: "hamburger",
                    },
                    subcategory: "section",
                  });
                }}
              >
                <IconMenuContainer>
                  <IconMenu />
                </IconMenuContainer>
              </ActionButton>
            </IdleQueue>
          </IconMenuPlaceholderContainer>
          {isMagazineStyle ? (
            <StyledStyleLogoContainer>
              <StyledLogoLink {...homepageLogoLinkProps}>
                <StyledScmpStyleLogoFlag />
              </StyledLogoLink>
              <StyledLogoLink {...styleLogoLinkProps}>
                <StyledStyleLogoText />
              </StyledLogoLink>
            </StyledStyleLogoContainer>
          ) : (
            <StyledLogoLink {...homepageLogoLinkProps}>
              <StyledScmpLogoFlag />
            </StyledLogoLink>
          )}
          {isTabletUp && renderFeatures("left")}
        </LeftFeatureContainer>
        <MobileStyledLogoLink {...homepageLogoLinkProps}>
          <StyledLogoWithText />
        </MobileStyledLogoLink>
        <NoSsr>
          <StyledDynamicHeaderOnelineMenuContainer appBarVariant={appBarVariant} />
        </NoSsr>
        <RightFeatureContainer $shouldSwap={shouldSwap}>
          {renderFeatures("right")}
        </RightFeatureContainer>
      </HeaderContainer>
    </Container>
  );
};

Header.displayName = "Header";
